import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine} from '@dropbox/dig-icons/assets';
import {KnowledgeSearchResult, NewsPost} from 'client';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/** A knowledge result's second line: its excerpt, else the site it opens. */
export const knowledgeSubtitle = (result: KnowledgeSearchResult) =>
  result.excerpt?.trim() || (result.external ? hostOf(result.url) : '');

/** A news post's second line, as today's box shows it: its category and date. */
export const newsSubtitle = (post: NewsPost) => {
  const date = new Date(post.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const category = post.category[0]?.name;
  return category ? `${category} • ${date}` : date;
};

/** A news post in the pane: its title, category and date, and a way to read it. */
export const NewsPreview = ({post, onOpen}: {post: NewsPost; onOpen: () => void}) => (
  <Stack gap="16">
    <Title size={18}>{post.title}</Title>
    <Text color="subtle">{newsSubtitle(post)}</Text>
    <Box>
      <Button variant="opacity" size="small" onClick={onOpen}>
        {t('search_box_read_post')}
      </Button>
    </Box>
  </Stack>
);

/**
 * A knowledge article or link in the pane: its title, the excerpt search
 * matched, and a way to open it (in a new tab when it lives elsewhere).
 */
export const KnowledgePreview = ({
  result,
  onOpen,
}: {
  result: KnowledgeSearchResult;
  onOpen: () => void;
}) => (
  <Stack gap="16">
    <Title size={18}>{result.title}</Title>
    {knowledgeSubtitle(result) && <Text color="subtle">{knowledgeSubtitle(result)}</Text>}
    <Box>
      <Button
        variant="opacity"
        size="small"
        withIconEnd={result.external ? <UIIcon src={OpenLine} /> : undefined}
        onClick={onOpen}
      >
        {t(result.kind === 'link' ? 'search_box_open_link' : 'search_box_open_article')}
      </Button>
    </Box>
  </Stack>
);
